/**
 * Client helper to request right-sized WebP image variants generated server-side
 * (see gist-firebase-functions src/utils/imageVariants.js). Variants are additive:
 * `<base>_<size>.webp` next to the original, served as a public GCS object. ALWAYS
 * pair variantURL() with onError={fallbackToOriginal()} so a missing variant (e.g.
 * not yet backfilled, or an unsupported source) falls back to the full-res original
 * instead of showing a broken image.
 *
 * The sizes the server generates are VARIANT_SIZES_BY_PREFIX below, and the path
 * scheme is variantObjectPath(). Both are pinned to src/contracts/imageVariants.json
 * by imageVariant.contract.test.js; the backend pins VARIANT_SPECS to its own copy
 * of that file (GIS-182). Ask only for a size listed for the image's prefix: any
 * other size 404s and the fallback quietly loads the multi-MB original.
 *
 * Recommended sizes by context:
 *   profile avatars/lists  -> 200      profile detail / host card -> 512
 *   event feed card        -> 400      event detail hero          -> 800
 *   chat bubble            -> 600      map markers                -> 200
 *
 * Only these tiers exist, per prefix (VARIANT_SPECS on the server):
 *   profile/ 200, 512 · event/ 400, 800 · eventSuggestions/ 400, 800, 1440 · chatImages/ 600
 * Any other size is a guaranteed 404 plus a second request for the original.
 */

/** Longest-edge sizes the server generates per storage prefix, ascending. */
export const VARIANT_SIZES_BY_PREFIX = Object.freeze({
  "profile/": Object.freeze([200, 512]),
  "event/": Object.freeze([400, 800]),
  "eventSuggestions/": Object.freeze([400, 800, 1440]),
  "chatImages/": Object.freeze([600]),
});

// Pull bucket + object path out of any Firebase/GCS URL we store (token download
// URLs, public GCS URLs). Returns null for anything we don't recognize.
function parseStorageURL(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('firebasestorage')) {
      // /v0/b/{bucket}/o/{ENCODED_PATH}?...&token={rotates-on-overwrite}
      const m = u.pathname.match(/\/b\/([^/]+)\/o\/(.+)$/);
      if (m) return { bucket: m[1], path: decodeURIComponent(m[2]), token: u.searchParams.get('token') || null };
    }
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      if (bucket && parts.length) return { bucket, path: decodeURIComponent(parts.join('/')), token: null };
    }
  } catch (_) { /* not a parseable URL */ }
  return null;
}

/** profile/uid/photo.jpg + 200 -> profile/uid/photo_200.webp (the server's variantPath). */
export function variantObjectPath(path, size) {
  const lastSlash = path.lastIndexOf('/');
  const lastDot = path.lastIndexOf('.');
  const base = lastDot > lastSlash ? path.slice(0, lastDot) : path;
  return `${base}_${size}.webp`;
}

/**
 * URL for the {size} variant of an image URL, or the original URL unchanged if it
 * can't be parsed or is already a variant.
 */
export function variantURL(url, size) {
  const parsed = parseStorageURL(url);
  if (!parsed || !parsed.path) return url;
  if (/_\d+\.webp$/i.test(parsed.path)) return url; // already a variant
  const vpath = variantObjectPath(parsed.path, size)
    .split('/')
    .map(encodeURIComponent)
    .join('/');
  let out = `https://storage.googleapis.com/${parsed.bucket}/${vpath}`;
  // Carry the original download URL's token as a cache-bust. For an overwritable
  // path (profile/{uid}/photo.jpg) the token rotates when the photo changes, so the
  // variant URL changes too and a new photo is picked up immediately. For immutable
  // media the token never changes, so this is a no-op.
  if (parsed.token) out += `?v=${parsed.token}`;
  return out;
}

// ---- Fallback observability (GIS-182) ------------------------------------------
// A variant that 404s still renders (the original loads), so drift between this
// table and the server is invisible on screen. Each fallback is reported once per
// original path per page load, with a hard cap so a bucket-wide gap (an unfinished
// backfill) cannot flood analytics from one session.
//
// The reporter is INJECTED by src/analytics/index.js when it loads, rather than
// imported here, so this module stays free of the analytics SDK: every component
// that renders an image imports it, and their tests should not have to load PostHog
// (this repo's jest config does not transform posthog-js, so they would fail to run).
const VARIANT_SUFFIX_RE = /_(\d+)\.webp$/i;
const MAX_FALLBACK_REPORTS = 20;
const reportedFallbacks = new Set();
let fallbackReporter = null;

/** Called by the analytics module with `(properties) => capture(...)`. */
export function setVariantFallbackReporter(reporter) {
  fallbackReporter = typeof reporter === 'function' ? reporter : null;
}

function prefixForPath(path) {
  return Object.keys(VARIANT_SIZES_BY_PREFIX).find((prefix) => path.startsWith(prefix)) || null;
}

/** Report that `failedSrc` (a variant URL) errored and `originalUrl` was loaded instead. */
export function reportVariantFallback(failedSrc, originalUrl) {
  try {
    if (!fallbackReporter) return;
    // Offline, every image errors; that is not drift.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const failed = parseStorageURL(failedSrc);
    const original = parseStorageURL(originalUrl);
    if (!failed || !original) return;
    const sizeMatch = failed.path.match(VARIANT_SUFFIX_RE);
    if (!sizeMatch) return; // the failing src was not a variant we built
    if (reportedFallbacks.has(original.path) || reportedFallbacks.size >= MAX_FALLBACK_REPORTS) return;
    reportedFallbacks.add(original.path);

    const size = Number(sizeMatch[1]);
    const prefix = prefixForPath(original.path);
    const ext = original.path.match(/\.([a-z0-9]+)$/i);
    // Never the path itself: profile paths carry a uid, and legacy ones a phone number.
    fallbackReporter({
      prefix: prefix || 'other',
      size,
      sizeGenerated: prefix ? VARIANT_SIZES_BY_PREFIX[prefix].includes(size) : false,
      sourceExt: ext ? ext[1].toLowerCase() : 'none',
    });
  } catch (_) { /* observability must never break an image */ }
}

export function resetVariantFallbackReportsForTests() {
  reportedFallbacks.clear();
}

/**
 * onError handler that swaps a failed variant <img> to its original exactly once.
 * Usage: <img src={variantURL(url, 200)} onError={fallbackToOriginal(url)} />
 *
 * onOriginalError, if given, runs when there is nothing left to fall back to: the
 * original failed too, or variantURL passed the URL through unchanged (a blob or
 * data URL, a non-storage host).
 */
export function fallbackToOriginal(originalUrl, onOriginalError) {
  return (e) => {
    const img = e && e.target;
    if (!img) return;
    // Mark the element rather than trusting the src comparison alone. img.src is
    // the browser-normalized URL and can differ from the stored string, and
    // clearing img.onerror does not detach React's onError, so without the marker
    // a dead original would be retried forever. Keyed on the URL so a reused
    // element showing a different photo still gets its own fallback.
    if (originalUrl && img.src !== originalUrl && img.__variantFallbackFor !== originalUrl) {
      const failedSrc = img.src;
      img.__variantFallbackFor = originalUrl;
      img.onerror = null;
      img.src = originalUrl;
      reportVariantFallback(failedSrc, originalUrl);
      return;
    }
    if (onOriginalError) onOriginalError(e);
  };
}
